import { useQuery } from '@tanstack/react-query';
import { configApi } from '../../configApi';
import { IDateType } from '../Types';
import { isValidUUID } from '../../../utils/functions';
import { IStatusProps } from '../SelectiveProcess/Types';
import { IResponseIdName } from '../globalQuery';

interface ICandidateResource {
  avatar: string;
  cellphone: string;
  cpf: string;
  date_of_birth: IDateType;
  document_number: string;
  document_type: string;
  email: string;
  id: string;
  is_foreign: false;
  mother_name: string;
  name: string;
  nis: string;
  passport_number: string;
  phone: string;
  redome_code: string;
}

interface ITypeResource {
  id: string;
  name: string;
  against_examination_board: boolean;
  has_exemption: boolean;
  has_question: boolean;
  against_title: boolean;
  is_active: boolean;
}

interface IResourceRules {
  max_attached: number;
  size_attached: number;
  has_attachment: boolean;
  is_required_attachment: boolean;
}

interface IProofSubject {
  id: string;
  name: string;
}

interface IMoreDetailResource {
  id: string;
  resource_code: number;
  candidate: ICandidateResource;
  registration_code: string;
  registration_id: string;
  resource_period_date?: string;
  resource_period_id: string;
  selective_process: ISelectiveProcess;
  created_at: IDateType;
  observation: string;
  status: IStatus;
  justification: string;
  resource_type: ITypeResource;
  ccv_response?: string;
  proof_link?: string;
  attachments: {
    resource_file: IResourceFile[];
  };
  vacancy: IVacancyProps;
  question: string;
  titles: {
    category?: IResponseIdName;
    item?: IResponseIdName;
  };
  proof_subject: IProofSubject;
  exemption: {
    cancel_registration_exemption: boolean;
    candidate: string;
    created_at: IDateType;
    id: string;
    internal_observation: null;
    is_active: true;
    is_declaration_veracity: true;
    justification: string;
    justification_date: IDateType;
    registration: string;
    selective_process: string;
    status: IStatusProps;
    show_nis_field: boolean;
    show_redome_field: boolean;
    type: IResponseIdName;
    updated_at: IDateType;
  };
  examination_board?: IResourceExaminationBoard;
  resource_rules: IResourceRules;
}

interface IResourceExaminationBoard {
  id: string;
  name: string;
  academic_unit: string;
  study_sector: string;
  status: string;
  member: {
    id: string;
    colaborator: string;
    colaborator_data: {
      name: string;
      cpf: string;
      email: string;
    };
    role: string;
    role_display: string;
  };
}

interface IStatus {
  id: string;
  name: string;
}

interface IVacancyProps {
  id: string;
  name: string;
  selective_process: ISelectiveProcess;
}

interface ISelectiveProcess {
  id: string;
  name: string;
  registrations_start: IDateType;
  registrations_end: IDateType;
  exemption_start: IDateType;
  exemption_end: IDateType;
  number_of_vacancies: number;
  public_notice_number: string;
  selective_process_situation: Iselective_process_situation[];
}

interface Iselective_process_situation {
  id: string;
  name: string;
}

export interface IResourceFile {
  id: string;
  name: string;
  url: string;
  size: number;
  sent_by_client: boolean;
  created_at: string;
  status: string;
}

const RequestMoreDetailResources = async (resourceId: string) => {
  if (isValidUUID(resourceId)) {
    const response = await configApi.get<IMoreDetailResource>(`resources/${resourceId}/`);
    return response.data;
  }
};

export const useRequestMoreDetailResources = (resourceId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['moreDetailResource', resourceId],
    queryFn: () => RequestMoreDetailResources(resourceId),
    refetchOnWindowFocus: false,
    gcTime: 0,
    enabled: enabled,
  });
};
